import React, { useEffect, useState } from "react";
import { api, fmtNum } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportXlsxMulti } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { DownloadSimple, CaretRight, ArrowCircleUp, ArrowCircleDown, Warning } from "@phosphor-icons/react";
import { KpiStrip, KpiCard } from "@finance/components/DesignSystem";
import { DueChip } from "@finance/components/DueChip";

function AgingTable({ data, label, testid }) {
  const [open, setOpen] = useState({});
  if (!data) return null;
  const toggle = (p) => setOpen((o) => ({ ...o, [p]: !o[p] }));
  const cols = [
    { key: "party", label: label }, { key: "0-30", label: "0-30", format: fmtNum },
    { key: "31-60", label: "31-60", format: fmtNum }, { key: "61-90", label: "61-90", format: fmtNum },
    { key: "90+", label: "90+", format: fmtNum }, { key: "total", label: "Total", format: fmtNum },
  ];
  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button variant="outline" data-testid={`export-${testid}`} onClick={() => exportXlsxMulti(`aging-${testid}`, [
          { name: "Summary", rows: data.rows, columns: cols, total: true },
          { name: "Detail", rows: data.rows.flatMap((r) => (r.items || []).map((it) => ({ party: r.party, ref: it.ref, kind: it.kind, date: it.date, days: it.days, bucket: it.bucket, amount: it.amount }))),
            columns: [{ key: "party", label: label }, { key: "ref", label: "Reference" }, { key: "kind", label: "Type" }, { key: "date", label: "Date" }, { key: "days", label: "Days", format: fmtNum }, { key: "bucket", label: "Bucket" }, { key: "amount", label: "Amount", format: fmtNum }] },
        ])}><DownloadSimple size={16} className="mr-1" />Export</Button>
      </div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            <th className="px-4 py-2 text-left">{label}</th>
            <th className="px-4 py-2 text-right">0-30</th><th className="px-4 py-2 text-right">31-60</th>
            <th className="px-4 py-2 text-right">61-90</th><th className="px-4 py-2 text-right">90+</th>
            <th className="px-4 py-2 text-right">Total</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Nothing outstanding.</td></tr>}
            {data.rows.map((r) => (
              <React.Fragment key={r.party}>
                <tr data-testid={`aging-row-${r.party}`} onClick={() => toggle(r.party)} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">
                    <CaretRight size={13} weight="bold" className={`mr-1.5 inline-block text-gray-400 transition-transform ${open[r.party] ? "rotate-90" : ""}`} />
                    {r.party}
                    <span className="ml-2 text-xs font-normal text-gray-400">({(r.items || []).length})</span>
                  </td>
                  <td className="px-4 py-2 text-right lx-num">{fmtNum(r["0-30"], "TZS")}</td>
                  <td className="px-4 py-2 text-right lx-num">{fmtNum(r["31-60"], "TZS")}</td>
                  <td className="px-4 py-2 text-right lx-num">{fmtNum(r["61-90"], "TZS")}</td>
                  <td className={`px-4 py-2 text-right lx-num ${r["90+"] > 0 ? "font-semibold text-red-600" : ""}`}>{fmtNum(r["90+"], "TZS")}</td>
                  <td className="px-4 py-2 text-right lx-num font-semibold">{fmtNum(r.total, "TZS")}</td>
                </tr>
                {open[r.party] && (r.items || []).map((it, idx) => (
                  <tr key={r.party + idx} data-testid={`aging-detail-${r.party}-${idx}`} className="border-b border-gray-50 bg-gray-50/60 text-xs">
                    <td className="py-1.5 pl-10 pr-4 text-gray-600">
                      <span className="mr-2 rounded-sm bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-500 ring-1 ring-gray-200">{it.kind}</span>
                      {it.ref}
                      <span className="ml-2 text-gray-400">{it.date} · {it.days}d</span>
                      {it.due_date && <DueChip date={it.due_date} testid={`aging-due-chip-${r.party}-${idx}`} />}
                    </td>
                    {["0-30", "31-60", "61-90", "90+"].map((bk) => (
                      <td key={bk} className={`py-1.5 pr-4 text-right lx-num ${bk === "90+" ? "text-red-500" : "text-gray-600"}`}>{it.bucket === bk ? fmtNum(it.amount, "TZS") : ""}</td>
                    ))}
                    <td className="py-1.5 pr-4 text-right lx-num text-gray-700">{fmtNum(it.amount, "TZS")}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr className="border-t-2 border-gray-300 font-bold">
              <td className="px-4 py-2">TOTAL</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.totals["0-30"], "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.totals["31-60"], "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.totals["61-90"], "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num">{fmtNum(data.totals["90+"], "TZS")}</td>
              <td className="px-4 py-2 text-right lx-num text-[#1B4383]">{fmtNum(data.totals.total, "TZS")}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function ForwardAgingTable({ data }) {
  if (!data) return <div className="text-sm text-gray-400">Loading…</div>;
  const b = data.buckets;
  return (
    <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
          <th className="px-4 py-2 text-left">Client</th>
          {b.map((k) => <th key={k} className="px-4 py-2 text-right">{k}</th>)}
          <th className="px-4 py-2 text-right">Total</th>
        </tr></thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={b.length + 2} className="px-4 py-8 text-center text-gray-400">Nothing outstanding.</td></tr>}
          {data.rows.map((r) => (
            <tr key={r.party} data-testid={`fwd-row-${r.party}`} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-2 font-medium">{r.party}</td>
              {b.map((k) => <td key={k} className={`px-4 py-2 text-right lx-num ${k === "Overdue" && r[k] > 0 ? "font-semibold text-red-600" : ""}`}>{fmtNum(r[k], "TZS")}</td>)}
              <td className="px-4 py-2 text-right lx-num font-semibold">{fmtNum(r.total, "TZS")}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-gray-300 font-bold">
            <td className="px-4 py-2">TOTAL</td>
            {b.map((k) => <td key={k} className="px-4 py-2 text-right lx-num">{fmtNum(data.totals[k], "TZS")}</td>)}
            <td className="px-4 py-2 text-right lx-num text-[#1B4383]">{fmtNum(data.totals.total, "TZS")}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Aging() {
  const [data, setData] = useState(null);
  const [fwd, setFwd] = useState(false);
  const [fwdData, setFwdData] = useState(null);
  useEffect(() => { api.get("/reports/aging").then((r) => setData(r.data)); }, []);
  useEffect(() => { if (fwd && !fwdData) api.get("/reports/aging-forward").then((r) => setFwdData(r.data)); }, [fwd, fwdData]);

  return (
    <Layout>
      <PageHeader title="Aged Analysis" subtitle="Payables & receivables — by age (overdue) or forward-looking by due date" />
      <div className="p-8">
        {data && (
          <KpiStrip cols={3}>
            <KpiCard icon={ArrowCircleUp} label="Total Payables" value={fmtNum(data.ap?.totals?.total || 0, "TZS")} tone="accent" testid="kpi-aging-ap" />
            <KpiCard icon={ArrowCircleDown} label="Total Receivables" value={fmtNum(data.ar_total_open ?? (data.ar?.totals?.total || 0), "TZS")} tone="info" testid="kpi-aging-ar" />
            <KpiCard icon={Warning} label="90+ Days Overdue" value={fmtNum((data.ap?.totals?.["90+"] || 0) + (data.ar?.totals?.["90+"] || 0), "TZS")} tone="danger" testid="kpi-aging-overdue" />
          </KpiStrip>
        )}
        <Tabs defaultValue="ap">
          <TabsList data-testid="aging-tabs">
            <TabsTrigger value="ap" data-testid="tab-ap">Payables (AP)</TabsTrigger>
            <TabsTrigger value="ar" data-testid="tab-ar">Receivables (AR)</TabsTrigger>
          </TabsList>
          <TabsContent value="ap" className="mt-6"><AgingTable data={data?.ap} label="Supplier" testid="ap" /></TabsContent>
          <TabsContent value="ar" className="mt-6">
            <div className="mb-3 flex items-center gap-2">
              <Button variant={fwd ? "outline" : "default"} size="sm" data-testid="ar-backward" onClick={() => setFwd(false)}>By age (overdue)</Button>
              <Button variant={fwd ? "default" : "outline"} size="sm" data-testid="ar-forward" onClick={() => setFwd(true)}>Forward (due dates)</Button>
            </div>
            {fwd ? <ForwardAgingTable data={fwdData} /> : <AgingTable data={data?.ar} label="Client" testid="ar" />}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
